import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CricketScoreSummary from '../components/cricket/CricketScoreSummary';
import CricketScoreSequence from '../components/cricket/CricketScoreSequence';
import TtScoreSummary from '../components/tabletennis/TtScoreSummary';
import TtScoreSequence from '../components/tabletennis/TtScoreSequence';
import TennisScoreSummary from '../components/tennis/TennisScoreSummary';
import TennisScoreSequence from '../components/tennis/TennisScoreSequence';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  Heading,
  Spinner,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Badge,
  useToast,
  Link,
  Container,
  IconButton,
  Collapse,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronDownIcon, ChevronUpIcon, SettingsIcon } from '@chakra-ui/icons';
import { 
  Market, 
  MarketType} from '../types/marketTypes';
import { EventStatus } from '../types/types';
import { getStatusColor, sportOf } from '../utils/eventUtils';
import { tennisMarketSections, ttCurrentSet, ttMarketSections } from '../utils/marketUtils';
import { formatDateTimeInTz } from '../utils/timezone';
import { useTimezone } from '../contexts/TimezoneContext';

// Import our custom hooks
import { useEventDetails } from '../hooks/useEventDetails';

// Import our reusable components
import { VideoPlayer } from '../components/events/VideoPlayer';
import { RecordingPanel } from '../components/events/RecordingPanel';
import { MarketSettlementDialog } from '../components/events/MarketSettlementDialog';
import { marketService } from '../services/marketService';
import { MarketSection } from '../components/events/MarketSection';
import { resendService } from '../services/resendService';
import RuleSetBadge from '../components/common/RuleSetBadge';

// Helper functions have been moved to eventUtils.ts

export const EventDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const eventId = id ? parseInt(id, 10) : 0;
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMarket, setSelectedMarket] = useState<Market | null>(null);
  const [selectedSelectionId, setSelectedSelectionId] = useState<number | null>(null);
  const [expandedInnings, setExpandedInnings] = useState<Record<string, boolean>>({});
  const [isResending, setIsResending] = useState(false);
  const [resendType, setResendType] = useState<string | null>(null);
  const [timeZone] = useTimezone();
  const toast = useToast();
  
  // Toggle inning expansion
  const toggleInning = (inning: string) => {
    setExpandedInnings(prev => ({
      ...prev,
      [inning]: !prev[inning]
    }));
  };
  
  // Responsive values
  const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const headingSize = useBreakpointValue({ base: 'sm', md: 'md' });
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Use our custom hook to manage event details and markets
  const {
    event,
    markets,
    isLoading,
    isMarketsLoading,
    isActionLoading,
    error,
    refreshEvent,
    refreshMarkets,
    cancelEvent,
    settleMarket
  } = useEventDetails(eventId);
  
  // Debug event and cricket scores
  React.useEffect(() => {
    if (event) {
      console.group('EventDetailsPage - Event Debug');
      console.log('Event:', event);
      console.log('Sport name:', event.sport?.name);
      console.log('Cricket scores available:', event.cricketScores ? 'Yes' : 'No');
      if (event.cricketScores) {
        console.log('Number of cricket scores:', event.cricketScores.length);
      }
      console.groupEnd();
    }
  }, [event]);

  // Group markets by their marketType for display
  const groupedMarkets = markets.reduce((acc, market) => {
    (acc[market.marketType] = acc[market.marketType] || []).push(market);
    return acc;
  }, {} as Record<string, Market[]>);

  const areAllMarketsSettled = () => {
    if (markets.length === 0) return false;
    return markets.every(market => market.marketOutcome === 'FINISHED');
  };

  const areAllMarketsVoided = () => {
    if (markets.length === 0) return false;
    return markets.every(market => market.marketOutcome === 'VOIDED');
  };

  const shouldShowVideo = () => {
    if (!event?.videoUrl || event.videoUrl.trim() === '' || event.status !== EventStatus.ACTIVE) {
      return false;
    }
    if (markets.length === 0) return true;
    return !areAllMarketsSettled() && !areAllMarketsVoided();
  };

  const getHeaderBackgroundColor = () => {
    if (markets.length === 0) return 'gray.50';
    
    const hasFinished = markets.some(market => market.marketOutcome === 'FINISHED');
    const allFinishedOrVoided = markets.every(market => 
      market.marketOutcome === 'FINISHED' || market.marketOutcome === 'VOIDED'
    );
    
    if (hasFinished && allFinishedOrVoided) return 'green.50';
    if (areAllMarketsSettled()) return 'green.50';
    if (areAllMarketsVoided()) return 'red.50';
    return 'gray.50';
  };
  
  // Sort Inning Runs markets: First Inning first
  if (groupedMarkets[MarketType.INNING_RUNS]) {
    groupedMarkets[MarketType.INNING_RUNS].sort((a, b) => {
      const aRef = a.reference || '';
      const bRef = b.reference || '';
      
      if (aRef === 'FIRST_INNING') return -1;
      if (bRef === 'FIRST_INNING') return 1;
      if (aRef === 'SECOND_INNING') return -1;
      if (bRef === 'SECOND_INNING') return 1;
      
      return 0;
    });
  }
  
  // Sort Team Boundaries: 4s then 6s
  const sortBoundaries = (markets: Market[]) => {
    return markets.sort((a, b) => {
      if (a.marketType === b.marketType) return 0;
      if (a.marketType === MarketType.TEAM_4S || a.marketType === MarketType.TOTAL_4S) return -1;
      return 1;
    });
  };
  
  if (groupedMarkets[MarketType.TEAM_4S] || groupedMarkets[MarketType.TEAM_6S]) {
    const teamBoundaries = [
      ...(groupedMarkets[MarketType.TEAM_4S] || []),
      ...(groupedMarkets[MarketType.TEAM_6S] || [])
    ];
    sortBoundaries(teamBoundaries);
  }
  
  if (groupedMarkets[MarketType.TOTAL_4S] || groupedMarkets[MarketType.TOTAL_6S]) {
    const matchBoundaries = [
      ...(groupedMarkets[MarketType.TOTAL_4S] || []),
      ...(groupedMarkets[MarketType.TOTAL_6S] || [])
    ];
    sortBoundaries(matchBoundaries);
  }
  
  // Parse reference values helper function
  const parseReference = (ref: string | undefined): [number, number, number] => {
    if (!ref) return [0, 0, 0];
    
    const parts = ref.split(':');
    const inning = parseInt(parts[0]) || 0;
    const over = parseInt(parts[1]) || 0;
    const ball = parseInt(parts[2]) || 0;
    
    return [inning, over, ball];
  };
  
  // Sort and group Next Ball markets by inning number from reference field
  let nextBallMarketsByInning: Record<number, Market[]> = {};
  
  if (groupedMarkets[MarketType.NEXT_BALL]) {
    // First sort all Next Ball markets
    groupedMarkets[MarketType.NEXT_BALL].sort((a, b) => {
      const [aInning, aOver, aBall] = parseReference(a.reference);
      const [bInning, bOver, bBall] = parseReference(b.reference);
      
      // Sort by inning first, then over, then ball
      if (aInning !== bInning) return aInning - bInning;
      if (aOver !== bOver) return aOver - bOver;
      return aBall - bBall;
    });
    
    // Then group by inning
    nextBallMarketsByInning = groupedMarkets[MarketType.NEXT_BALL].reduce((acc, market) => {
      const [inning] = parseReference(market.reference);
      (acc[inning] = acc[inning] || []).push(market);
      return acc;
    }, {} as Record<number, Market[]>);
  }
  
  // Use a ref to track the previous innings keys to avoid unnecessary re-renders
  const prevInningsKeysRef = useRef<string>('');
  
  // Initialize expanded innings state when markets change
  useEffect(() => {
    // Only run this effect when markets are loaded
    if (!isMarketsLoading && markets.length > 0) {
      // Create a string key representing the current set of innings
      const inningKeysString = Object.keys(nextBallMarketsByInning).sort().join(',');
      
      // Only update if we have innings and they've changed from previous render
      if (inningKeysString && inningKeysString !== prevInningsKeysRef.current) {
        // By default, expand the latest inning and collapse others
        const innings = Object.keys(nextBallMarketsByInning).sort((a, b) => parseInt(b) - parseInt(a));
        const newExpandedState: Record<string, boolean> = {};
        
        // Set all innings to expanded by default
        innings.forEach((inning) => {
          newExpandedState[inning] = true; // All innings expanded
        });
        
        setExpandedInnings(newExpandedState);
        prevInningsKeysRef.current = inningKeysString;
      }
    }
  }, [isMarketsLoading, markets.length]); // Only depend on markets loading state and length

  // Market action handlers
  const handleOpenMarket = async (marketId: number) => {
    try {
      await marketService.openMarket(marketId);
      refreshMarkets();
    } catch (error) {
      console.error('Failed to open market:', error);
    }
  };

  const handleCloseMarket = async (marketId: number) => {
    try {
      await marketService.closeMarket(marketId);
      refreshMarkets();
    } catch (error) {
      console.error('Failed to close market:', error);
    }
  };

  const handleVoidMarket = async (marketId: number) => {
    try {
      await marketService.voidMarket(marketId);
      refreshMarkets();
    } catch (error) {
      console.error('Failed to void market:', error);
    }
  };
  
  const handleUnvoidMarket = async (marketId: number) => {
    try {
      await marketService.unvoidMarket(marketId);
      refreshMarkets();
    } catch (error) {
      console.error('Failed to unvoid market:', error);
    }
  };
  
  const handleSettleMarket = (market: Market) => {
    if (market.selections.length > 0) {
      // Open the dialog to select winning outcome
      setSelectedMarket(market);
      setSelectedSelectionId(null); // Reset selection
      onOpen();
    } else {
      console.error('Cannot settle market with no selections');
    }
  };
  
  const confirmSettleMarket = async () => {
    if (selectedMarket && selectedSelectionId) {
      try {
        await settleMarket(selectedMarket.id, selectedSelectionId);
        onClose();
      } catch (error) {
        console.error('Failed to settle market:', error);
      }
    } else {
      console.error('No selection made');
    }
  };
  
  const handleUnsettleMarket = async (marketId: number) => {
    try {
      await marketService.unsettleMarket(marketId);
      refreshMarkets();
    } catch (error) {
      console.error('Failed to unsettle market:', error);
    }
  };

  const handleCancel = async () => {
    await cancelEvent();
  };

  const handleResendEvent = async () => {
    setIsResending(true);
    setResendType('event');
    try {
      await resendService.resendEvent(eventId);
      toast({
        title: "Event resent",
        description: `Event ${eventId} has been resent successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to resend event:', error);
      toast({
        title: "Error",
        description: "Failed to resend event. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
      setResendType(null);
    }
  };

  const handleResendMarkets = async () => {
    setIsResending(true);
    setResendType('markets');
    try {
      await resendService.resendMarkets(eventId);
      toast({
        title: "Markets resent",
        description: `Markets for event ${eventId} have been resent successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to resend markets:', error);
      toast({
        title: "Error",
        description: "Failed to resend markets. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
      setResendType(null);
    }
  };

  const handleResendResults = async () => {
    setIsResending(true);
    setResendType('results');
    try {
      await resendService.resendResults(eventId);
      toast({
        title: "Results resent",
        description: `Results for event ${eventId} have been resent successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to resend results:', error);
      toast({
        title: "Error",
        description: "Failed to resend results. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
      setResendType(null);
    }
  };

  const handleResendScores = async () => {
    setIsResending(true);
    setResendType('scores');
    try {
      await resendService.resendScores(eventId);
      toast({
        title: "Scores resent",
        description: `Scores for event ${eventId} have been resent successfully.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to resend scores:', error);
      toast({
        title: "Error",
        description: "Failed to resend scores. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsResending(false);
      setResendType(null);
    }
  };

  return (
    <Container maxW="container.xl" py={{ base: 3, md: 5 }} px={{ base: 2, md: 4 }}>
      {/* Settlement Dialog */}
      {selectedMarket && (
        <MarketSettlementDialog
          isOpen={isOpen}
          onClose={onClose}
          market={selectedMarket}
          onSettle={(marketId, winningOutcomeId) => settleMarket(marketId, winningOutcomeId)}
          isLoading={isActionLoading}
        />
      )}
      
      {/* Back Button and Action Buttons */}
      <Flex mb={{ base: 3, md: 5 }} justify="space-between" align="center" gap={2} flexWrap="wrap">
        <Button 
          leftIcon={<ChevronLeftIcon />} 
          onClick={() => navigate('/events')} 
          variant="outline"
          size={buttonSize}
        >
          Back to Events
        </Button>
        
        <Flex gap={2} align="center">
          <Button 
            colorScheme="red" 
            size={buttonSize} 
            onClick={handleCancel}
            isDisabled={isResending}
          >
            Cancel Event
          </Button>
          
          <Menu closeOnSelect={true}>
            <MenuButton
              as={IconButton}
              icon={<SettingsIcon />}
              colorScheme="blue"
              size={buttonSize}
              isDisabled={isResending}
              aria-label="Resend options"
              isLoading={isResending}
            />
            <MenuList>
              <MenuItem onClick={handleResendEvent}>
                Resend Event
              </MenuItem>
              <MenuItem onClick={handleResendMarkets}>
                Resend Markets
              </MenuItem>
              <MenuItem onClick={handleResendResults}>
                Resend Results
              </MenuItem>
              <MenuItem onClick={handleResendScores}>
                Resend Scores
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
      
      {/* Loading Spinner for Event Data */}
      {isLoading ? (
        <Center py={{ base: 6, md: 10 }}>
          <VStack spacing={{ base: 3, md: 4 }}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text>Loading event details...</Text>
          </VStack>
        </Center>
      ) : (
        <>
          <Box mb={{ base: 4, md: 6 }} p={{ base: 3, md: 4 }} borderWidth="1px" borderRadius="lg" bg={getHeaderBackgroundColor()}>
            <Flex 
              direction={{ base: 'column', sm: 'row' }} 
              justify="space-between" 
              align={{ base: 'flex-start', sm: 'flex-start' }}
              gap={{ base: 3, sm: 3 }}
            >
              <VStack align="start" spacing={1} flex="1" minW={{ base: '100%', sm: '250px' }}>
                <Heading size={headingSize}>{event?.name ?? ''}</Heading>
                
                {/* Compact score in header, per sport */}
                {(() => {
                  const kind = sportOf(event);

                  if (kind === 'CRICKET' && event?.cricketScores && event.cricketScores.length > 0) {
                    return (
                      <Box mt={1}>
                        <CricketScoreSummary
                          cricketScores={event.cricketScores}
                          firstCompetitor={event.firstCompetitor}
                          secondCompetitor={event.secondCompetitor}
                          compact={true}
                        />
                      </Box>
                    );
                  }

                  if (kind === 'TENNIS' && event?.tennisScores && event.tennisScores.length > 0) {
                    return (
                      <Box mt={1}>
                        <TennisScoreSummary
                          tennisScores={event.tennisScores}
                          firstCompetitor={event.firstCompetitor}
                          secondCompetitor={event.secondCompetitor}
                          ruleSet={event.competition?.ruleSet}
                        />
                      </Box>
                    );
                  }

                  if (kind === 'TABLE_TENNIS' && event?.ttScores && event.ttScores.length > 0) {
                    return (
                      <Box mt={1}>
                        <TtScoreSummary
                          ttScores={event.ttScores}
                          firstCompetitor={event.firstCompetitor}
                          secondCompetitor={event.secondCompetitor}
                          compact={true}
                        />
                      </Box>
                    );
                  }
                  return null;
                })()}
                
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text color="gray.600" fontSize="sm">{event ? formatDateTimeInTz(event.startTime, timeZone) : ''}</Text>
                  <Badge colorScheme={event ? getStatusColor(event.status) : 'gray'}>
                    {event ? EventStatus[event.status] : ''}
                  </Badge>
                </Flex>
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text fontSize="sm" color="gray.600">{event?.competition?.name ?? ''}</Text>
                  <Text fontSize="sm" color="gray.600" display={{ base: 'none', sm: 'inline' }}>•</Text>
                  <Text fontSize="sm" color="gray.600">{event?.sport?.name ?? ''}</Text>
                  <Text fontSize="sm" color="gray.600" display={{ base: 'none', sm: 'inline' }}>•</Text>
                  <RuleSetBadge ruleSet={event?.competition?.ruleSet} />
                  {event?.competition && (
                    <Badge colorScheme={event.competition.marginLevel ? 'teal' : 'gray'}>
                      margin: {event.competition.marginLevel ?? 'default'}
                    </Badge>
                  )}
                </Flex>
                <Flex flexWrap="wrap" gap={2} align="center">
                  <Text fontSize="sm" color="gray.500">
                    ID: {sportOf(event) === 'TENNIS' ? event?.providerEventId : (
                      <>
                        {event?.providerEventId ? event.providerEventId.slice(0, -1) : ''}
                        <Text as="span" fontWeight="bold" color="blue.600">
                          {event?.providerEventId ? event.providerEventId.slice(-1) : ''}
                        </Text>
                      </>
                    )}
                  </Text>
                  <Text fontSize="sm" color="gray.500" display={{ base: 'none', sm: 'inline' }}>•</Text>
                  <Text fontSize="sm" color="gray.500">Provider: {event?.provider?.name ?? ''}</Text>
                </Flex>
              </VStack>
              
              {shouldShowVideo() && event?.videoUrl ? (
                <Box width={{ base: '100%', md: '300px' }}>
                  <Heading size="xs" mb={1}>Live Stream</Heading>
                  <VideoPlayer 
                    videoUrl={event.videoUrl} 
                    title={event?.firstCompetitor?.name && event?.secondCompetitor?.name ? 
                      `${event.firstCompetitor.name} vs ${event.secondCompetitor.name}` : 'Event Stream'}
                  />
                  <Link 
                    href={event.videoUrl} 
                    isExternal 
                    fontSize="xs" 
                    color="blue.500" 
                    mt={1} 
                    display="inline-block"
                  >
                    Open Stream
                  </Link>
                </Box>
              ) : (
                <RecordingPanel
                  matchId={event?.providerEventId}
                  startTime={event?.startTime}
                  title={event?.firstCompetitor?.name && event?.secondCompetitor?.name ?
                    `${event.firstCompetitor.name} vs ${event.secondCompetitor.name}` : 'Match recording'}
                />
              )}
            </Flex>
          </Box>

          {/* Markets Section */}
          {isMarketsLoading ? (
            <Center py={{ base: 6, md: 10 }}>
              <VStack spacing={{ base: 3, md: 4 }}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text>Loading markets...</Text>
              </VStack>
            </Center>
          ) : (
            <>
              {/* Score sequence section, per sport */}
              {(() => {
                const kind = sportOf(event);

                if (kind === 'CRICKET' && event?.cricketScores && event.cricketScores.length > 0) {
                  return (
                    <Box mb={3} p={2} borderWidth="1px" borderRadius="lg" bg="gray.50">
                      <CricketScoreSequence cricketScores={event.cricketScores} />
                    </Box>
                  );
                }

                if (kind === 'TENNIS' && event?.tennisScores && event.tennisScores.length > 0) {
                  return (
                    <Box mb={3} p={2} borderWidth="1px" borderRadius="lg" bg="gray.50">
                      <TennisScoreSequence
                        tennisScores={event.tennisScores}
                        firstCompetitor={event.firstCompetitor}
                        secondCompetitor={event.secondCompetitor}
                        ruleSet={event.competition?.ruleSet}
                      />
                    </Box>
                  );
                }

                if (kind === 'TABLE_TENNIS' && event?.ttScores && event.ttScores.length > 0) {
                  return (
                    <Box mb={3} p={2} borderWidth="1px" borderRadius="lg" bg="gray.50">
                      <TtScoreSequence
                        ttScores={event.ttScores}
                        firstCompetitor={event.firstCompetitor}
                        secondCompetitor={event.secondCompetitor}
                      />
                    </Box>
                  );
                }
                return null;
              })()}
              
              {/* Table Tennis markets: the match-level picks, then everything each set settles */}
              {sportOf(event) === 'TABLE_TENNIS'
                && ttMarketSections(groupedMarkets, ttCurrentSet(event?.ttScores)).map(group => (
                <MarketSection
                  key={group.title}
                  title={group.title}
                  markets={group.markets}
                  collapsible
                  defaultOpen={group.defaultOpen}
                  event={event ?? undefined}
                  isActionLoading={isActionLoading}
                  compact={isMobile}
                  onOpenMarket={handleOpenMarket}
                  onCloseMarket={handleCloseMarket}
                  onVoidMarket={handleVoidMarket}
                  onUnvoidMarket={handleUnvoidMarket}
                  onSettleMarket={handleSettleMarket}
                  onUnsettleMarket={handleUnsettleMarket}
                />
              ))}

              {/* Tennis markets: the match-level picks, then the games of each set */}
              {sportOf(event) === 'TENNIS' && tennisMarketSections(groupedMarkets).map(group => (
                <MarketSection
                  key={group.title}
                  title={group.title}
                  markets={group.markets}
                  event={event ?? undefined}
                  isActionLoading={isActionLoading}
                  compact={isMobile}
                  onOpenMarket={handleOpenMarket}
                  onCloseMarket={handleCloseMarket}
                  onVoidMarket={handleVoidMarket}
                  onUnvoidMarket={handleUnvoidMarket}
                  onSettleMarket={handleSettleMarket}
                  onUnsettleMarket={handleUnsettleMarket}
                />
              ))}

              {/* Cricket markets layout */}
              {sportOf(event) === 'CRICKET' && (
              <>
              {/* First Row: Two Columns */}
              <Grid
                templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
                gap={3}
                mb={3}
              >
                {/* Left Column: Match Odds, Toss, and Boundaries */}
                <Box w="100%">
                  {/* Match Odds and Toss in horizontal layout */}
                  <Box mb={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Box>
                        <MarketSection
                          title="Match Odds"
                          markets={groupedMarkets[MarketType.TWO_WAY_MATCH_ODDS] || groupedMarkets[MarketType.THREE_WAY_MATCH_ODDS] || []}
                          event={event ?? undefined}
                          isActionLoading={isActionLoading}
                          compact={isMobile}
                          onOpenMarket={handleOpenMarket}
                          onCloseMarket={handleCloseMarket}
                          onVoidMarket={handleVoidMarket}
                          onUnvoidMarket={handleUnvoidMarket}
                          onSettleMarket={handleSettleMarket}
                          onUnsettleMarket={handleUnsettleMarket}
                        />
                      </Box>
                      <Box>
                        <MarketSection
                          title="Toss"
                          markets={groupedMarkets[MarketType.TOSS] || []}
                          event={event ?? undefined}
                          isActionLoading={isActionLoading}
                          compact={isMobile}
                          onOpenMarket={handleOpenMarket}
                          onCloseMarket={handleCloseMarket}
                          onVoidMarket={handleVoidMarket}
                          onUnvoidMarket={handleUnvoidMarket}
                          onSettleMarket={handleSettleMarket}
                          onUnsettleMarket={handleUnsettleMarket}
                        />
                      </Box>
                    </Grid>
                  </Box>

                  {/* Team Boundaries Section */}
                  <Box mb={3}>
                    <MarketSection
                      title="Team Boundaries"
                      markets={[
                        ...(groupedMarkets[MarketType.TEAM_4S] || []),
                        ...(groupedMarkets[MarketType.TEAM_6S] || [])
                      ]}
                      event={event ?? undefined}
                      isActionLoading={isActionLoading}
                      onOpenMarket={handleOpenMarket}
                      onCloseMarket={handleCloseMarket}
                      onVoidMarket={handleVoidMarket}
                      onUnvoidMarket={handleUnvoidMarket}
                      onSettleMarket={handleSettleMarket}
                      onUnsettleMarket={handleUnsettleMarket}
                    />
                  </Box>

                  {/* Match Boundaries Section */}
                  <Box>
                    <MarketSection
                      title="Match Boundaries"
                      markets={[
                        ...(groupedMarkets[MarketType.TOTAL_4S] || []),
                        ...(groupedMarkets[MarketType.TOTAL_6S] || [])
                      ]}
                      event={event ?? undefined}
                      isActionLoading={isActionLoading}
                      onOpenMarket={handleOpenMarket}
                      onCloseMarket={handleCloseMarket}
                      onVoidMarket={handleVoidMarket}
                      onUnvoidMarket={handleUnvoidMarket}
                      onSettleMarket={handleSettleMarket}
                      onUnsettleMarket={handleUnsettleMarket}
                    />
                  </Box>
                </Box>

                {/* Right Column: Innings Runs */}
                <Box w="100%">
                  <MarketSection
                      title="Innings Runs"
                      markets={groupedMarkets[MarketType.INNING_RUNS] || []}
                      event={event ?? undefined}
                      isActionLoading={isActionLoading}
                      gridColumns="repeat(2, 1fr)"
                      onOpenMarket={handleOpenMarket}
                      onCloseMarket={handleCloseMarket}
                      onVoidMarket={handleVoidMarket}
                      onUnvoidMarket={handleUnvoidMarket}
                      onSettleMarket={handleSettleMarket}
                      onUnsettleMarket={handleUnsettleMarket}
                  />

                  <Box>
                    {/* Total */}
                    <Box>
                      <MarketSection
                          title="Other Totals"
                          markets={[
                            ...(groupedMarkets[MarketType.MATCH_TOTAL_RUNS] || []),
                            ...(groupedMarkets[MarketType.MATCH_TOTAL_WICKETS] || [])
                          ]}
                          event={event ?? undefined}
                          isActionLoading={isActionLoading}
                          onOpenMarket={handleOpenMarket}
                          onCloseMarket={handleCloseMarket}
                          onVoidMarket={handleVoidMarket}
                          onUnvoidMarket={handleUnvoidMarket}
                          onSettleMarket={handleSettleMarket}
                          onUnsettleMarket={handleUnsettleMarket}
                      />
                    </Box>

                    {/* Odd/Even Section */}
                    <Box>
                      <MarketSection
                          title="Odd/Even"
                          markets={[
                            ...(groupedMarkets[MarketType.TEAM_ODD_EVEN] || []),
                            ...(groupedMarkets[MarketType.MATCH_ODD_EVEN] || [])
                          ]}
                          event={event ?? undefined}
                          isActionLoading={isActionLoading}
                          onOpenMarket={handleOpenMarket}
                          onCloseMarket={handleCloseMarket}
                          onVoidMarket={handleVoidMarket}
                          onUnvoidMarket={handleUnvoidMarket}
                          onSettleMarket={handleSettleMarket}
                          onUnsettleMarket={handleUnsettleMarket}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Second Row: Next Ball Markets Grouped by Innings */}
              <Box mb={3}>
                <Heading size={headingSize} mb={2}>Next Ball</Heading>
                
                {/* If there are no Next Ball markets */}
                {Object.keys(nextBallMarketsByInning).length === 0 && (
                  <Box p={4} borderWidth="1px" borderRadius="lg" bg="gray.50">
                    <Text align="center" color="gray.500">No Next Ball markets available</Text>
                  </Box>
                )}
                
                {/* Display markets grouped by innings */}
                {Object.entries(nextBallMarketsByInning)
                  .sort(([inningA], [inningB]) => parseInt(inningA) - parseInt(inningB))
                  .map(([inning, markets]) => (
                    <Box key={`inning-${inning}`} mb={4}>
                      <Flex 
                        align="center" 
                        bg="gray.100" 
                        p={2} 
                        borderRadius="md"
                        mb={2}
                        cursor="pointer"
                        onClick={() => toggleInning(inning)}
                      >
                        <Heading size="xs" flex="1">Inning {inning}</Heading>
                        <IconButton
                          aria-label={expandedInnings[inning] ? "Collapse inning" : "Expand inning"}
                          icon={expandedInnings[inning] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleInning(inning);
                          }}
                        />
                      </Flex>
                      <Collapse in={expandedInnings[inning] ?? true} animateOpacity>
                        <Box pt={1} pb={2}>
                          <MarketSection
                            title=""
                            markets={markets}
                            event={event ?? undefined}
                            isActionLoading={isActionLoading}
                            compact={true}
                            gridColumns={isMobile ? 'repeat(auto-fill, minmax(120px, 1fr))' : undefined}
                            onOpenMarket={handleOpenMarket}
                            onCloseMarket={handleCloseMarket}
                            onVoidMarket={handleVoidMarket}
                            onUnvoidMarket={handleUnvoidMarket}
                            onSettleMarket={handleSettleMarket}
                            onUnsettleMarket={handleUnsettleMarket}
                          />
                        </Box>
                      </Collapse>
                    </Box>
                  ))
                }
              </Box>
              </>
              )}

              {/* Whatever this frontend has no mapping for: shown by its own name, never as another type */}
              {(groupedMarkets[MarketType.UNKNOWN] || []).length > 0 && (
                <MarketSection
                  title="Unmapped markets"
                  markets={groupedMarkets[MarketType.UNKNOWN]}
                  event={event ?? undefined}
                  isActionLoading={isActionLoading}
                  compact={isMobile}
                  onOpenMarket={handleOpenMarket}
                  onCloseMarket={handleCloseMarket}
                  onVoidMarket={handleVoidMarket}
                  onUnvoidMarket={handleUnvoidMarket}
                  onSettleMarket={handleSettleMarket}
                  onUnsettleMarket={handleUnsettleMarket}
                />
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};
